import {
	action,
	computed,
	IReactionDisposer,
	observable,
	reaction,
	runInAction,
	when,
} from "mobx";
import { match } from "react-router-dom";
import { LazyImportWithLoadFailHandle as lazy } from "@lib/lazy-import-with-guard/LazyImportWithGuard";

import NodeStore from "@state/stores/offer/shared/NodeStore";
import { THEME_SETTINGS } from "@theme-settings";
import { FeaturedMenuElement, Item } from "@api-types/offer/FeaturedMenu";
import { getCurrentCulture } from "@utils";
import { history, redirect, SessionStorageProvider } from "@state";
import RootStore from "@state/stores/RootStore";
import { generatePrematchOfferMenuFilter } from "@utils/offer";
import { buildRoute } from "@utils/common/buildOfferRoute";
import PeriodConverter, { Period } from "@state/common/PeriodConverter";
import { BaseOfferMenuStore } from "../shared/BaseOfferMenuStore";
import { PrematchPeriodSubMenuStore } from "./PrematchPeriodSubMenuStore";
import { TPrematchSubMenuParent } from "@data-types/offer/prematch/TPrematchSubMenuParent";
import {
	FormattedMenuNode,
	SportMenuNodeType,
} from "@data-types/offer/shared/SportNodeType";
import { StorageStateKeysEnum } from "@utils/storage";
import { MenuResponseWithCache } from "@api-types/offer/PrematchMenuResponse";
import { IOfferService } from "@services/offer/IOfferService";
import { offerHistoryService } from "../../../../data/services/offer/offer-history/OfferHistoryService";
import { prematchOfferService } from "../../../../data/services/offer/PrematchOfferService";

const loadFailPath = `/${getCurrentCulture()}/app-update`;

// prettier-ignore
const PrematchOfferApiService = lazy(loadFailPath, ()=>import("@api-services/offer/PrematchOfferApiService"));

export default class PrematchOfferMenuStore
	extends BaseOfferMenuStore
	implements TPrematchSubMenuParent
{
	public type: "PrematchOfferMenuStore" = "PrematchOfferMenuStore";
	private urlSync: IReactionDisposer | null;
	protected waitForStoreInitialize: IReactionDisposer | null;

	public periodSubMenu: PrematchPeriodSubMenuStore;
	@observable public isProcessingParams: boolean = false;
	@observable public selectedSport: this["menu"] | null = null;
	@observable isUpdateProcessing: boolean = false;
	@observable public topTournamentTournamentIds: string[] | null;
	public rootStore: RootStore;

	@observable isFavoritesSubMenuOpen = false;

	@computed get period() {
		return this.periodSubMenu.selectedPeriodKey;
	}

	@computed get singleTournamentNode(): this["menu"] | null {
		const sel = this.menu.selectedItems;
		if (sel.tournaments.length !== 1) return null;
		if (sel.specials.length > 0) return null;
		if (sel.sports.length > 1 || sel.categories.length > 1) return null;
		const tid = sel.tournaments[0];
		if (!tid || tid === "special") return null;
		if (tid.startsWith("ss:") || tid.startsWith("tt:")) return null;
		const root = this.menu;
		const node = Array.from(root.selectedItemsSet?.values() || []).find(
			(n) =>
				n.node.type === "tournament" &&
				n.node.id === tid &&
				!n.node.isTopTournament
		) as this["menu"] | undefined;
		if (node == null) return null;
		const sportNode = node.parent?.parent;
		if (!sportNode?.node?.abrv) return null;
		if (!node.node.abrv) return null;
		return node;
	}

	@computed get isSingleTournamentSelected(): boolean {
		return this.singleTournamentNode != null;
	}

	//#region sideMenu actions overrides

	/** Sport node to be selected in main sports list. */
	@action.bound
	toggleSport(node: this["menu"]) {
		if (node?.node?.id === this.selectedSport?.node?.id) {
			this.deselectSport();
		} else {
			this.selectSport(node);
		}
	}

	/** Sport node to be selected in main sports list. */
	@action.bound
	selectSport(node: this["menu"]) {
		this.selectedSport = node;

		this.openOfferMenu();
	}

	/** Removes sport from side menu and closes side menu. */
	@action.bound
	deselectSport() {
		this.selectedSport = null;
		this.closeOfferMenu();
	}

	//#endregion sideMenu actions overrides

	//#region observable

	@observable menu = new NodeStore<SportMenuNodeType>({
		id: "",
		count: 0,
		type: "root",
		name: "",
	});

	//#endregion observable

	//#region constructor

	constructor(rootStore: RootStore) {
		super(rootStore);
		this.rootStore = rootStore;

		this.periodSubMenu = new PrematchPeriodSubMenuStore(this);
		this.startUrlSync();
	}

	//#endregion constructor

	//#region sport menu

	@observable isOfferMenuOpen = false;

	@computed get isExpandDisabled() {
		return this.menu.children.length <= 0;
	}

	@computed get apiService(): IOfferService {
		if (PeriodConverter.IsOfferHistoryPeriod(this.period)) {
			return offerHistoryService;
		}
		return prematchOfferService;
	}

	@action.bound
	uncheckAndCollapse() {
		this.menu.onCheck(false);
		this.menu.collapseTree();
	}

	@action.bound
	toggleOpenOfferMenu() {
		if (this.isOfferMenuOpen) {
			this.closeOfferMenu();
		} else {
			this.openOfferMenu();
		}
	}

	@action.bound
	openOfferMenu() {
		if (
			this.selectedSport === null &&
			this.menu?.children != null &&
			this.menu.children.length > 0
		) {
			this.selectedSport = this.menu?.children?.filter(
				(sport) => !sport.node.isTopTournament
			)?.[0];
		}

		this.isOfferMenuOpen = true;
		this.periodSubMenu.isPeriodMenuExpanded = false;
		this.isFavoritesSubMenuOpen = false;
		this.rootStore.openSideMenu();
	}

	@action.bound
	closeOfferMenu() {
		this.selectedSport = null;

		this.isOfferMenuOpen = false;
		this.rootStore.closeSideMenu();
	}
	@action.bound
	openFavoritesMenu() {
		if (!THEME_SETTINGS.features.isFavoritesEnabled) {
			return;
		}
		this.isOfferMenuOpen = false;
		this.periodSubMenu.isPeriodMenuExpanded = false;
		this.isFavoritesSubMenuOpen = true;
		this.deselectSport();
		this.rootStore.openSideMenu();
	}

	@action.bound
	closeFavoritesMenu() {
		if (!THEME_SETTINGS.features.isFavoritesEnabled) {
			return;
		}
		this.isOfferMenuOpen = false;
		this.periodSubMenu.isPeriodMenuExpanded = false;
		this.isFavoritesSubMenuOpen = false;
		this.rootStore.closeSideMenu();
	}

	@action.bound toggleFavoritesMenu() {
		if (this.isFavoritesSubMenuOpen) {
			this.closeFavoritesMenu();
		} else {
			this.openFavoritesMenu();
		}
	}
	@action.bound
	resetMenu() {
		this.menu.onCheck(false);
		this.menu.collapseTree();
	}

	//#endregion sport menu

	//#region fetching data

	@action.bound
	async fetchData() {
		if (!this.isFetchingData) {
			this.onStartFetching();
		}

		const fetchingForPeriod = this.periodSubMenu.selectedPeriodKey;
		const filter = generatePrematchOfferMenuFilter(fetchingForPeriod);

		let hash = "";
		let storedMenu = "";
		try {
			hash = JSON.parse(
				SessionStorageProvider.get(
					StorageStateKeysEnum.PREMATCH_MENU_HASH
				) || ""
			);

			storedMenu = JSON.parse(
				SessionStorageProvider.get(
					StorageStateKeysEnum.PREMATCH_MENU_DATA
				) || ""
			);
		} catch (e) {}
		try {
			const data = await this.apiService.getOfferMenu({
				Filter: filter,
				withFeatured: true,
				hash: hash && storedMenu ? hash : "",
			});

			let menu: MenuResponseWithCache<true>["value"] = {
				featuredMenu: [],
				fullMenu: [],
			};
			if (data.hash == hash) {
				if (storedMenu) {
					menu =
						storedMenu as unknown as MenuResponseWithCache<true>["value"];
				} else if (data.value) {
					menu = data.value;
					SessionStorageProvider.set(
						StorageStateKeysEnum.PREMATCH_MENU_DATA,
						JSON.stringify(data.value)
					);
					SessionStorageProvider.set(
						StorageStateKeysEnum.PREMATCH_MENU_HASH,
						JSON.stringify(data.hash)
					);
				} else {
					SessionStorageProvider.remove(
						StorageStateKeysEnum.PREMATCH_MENU_DATA
					);
					SessionStorageProvider.remove(
						StorageStateKeysEnum.PREMATCH_MENU_HASH
					);
				}
			} else {
				if (data.value) {
					menu = data.value;
					SessionStorageProvider.set(
						StorageStateKeysEnum.PREMATCH_MENU_DATA,
						JSON.stringify(data.value)
					);
					SessionStorageProvider.set(
						StorageStateKeysEnum.PREMATCH_MENU_HASH,
						JSON.stringify(data.hash)
					);
				} else {
					SessionStorageProvider.remove(
						StorageStateKeysEnum.PREMATCH_MENU_DATA
					);
					SessionStorageProvider.remove(
						StorageStateKeysEnum.PREMATCH_MENU_HASH
					);
				}
			}
			let formattedSpecialMenu: FormattedMenuNode[] | null = null;

			if (
				menu.featuredMenu &&
				Object.keys(menu.featuredMenu).length > 0
			) {
				formattedSpecialMenu = await this.initializeTopTournaments(
					menu.featuredMenu
				);
			}

			let formattedMenu: FormattedMenuNode[] = [];
			formattedMenu = this.formatMenu(menu.fullMenu);

			this.menu.assignStructure({
				n: {
					isRoot: true,
					id: "root",
					count:
						(formattedSpecialMenu?.length || 0) +
						formattedMenu.length,
					type: "root",
					name: "root",
				},
				c: [...(formattedSpecialMenu || []), ...formattedMenu],
			});
		} catch (e) {
			console.error(e);
			this.clearMenu();
		} finally {
			this.onBeforeFetchFinish();

			this.onDoneFetching();
		}
	}

	@action.bound
	clearMenu() {
		this.menu.assignStructure({
			n: {
				isRoot: true,
				id: "root",
				count: 0,
				type: "root",
				name: "root",
			},
			c: [],
		});
		this.selectedSport = null;
	}

	@action.bound
	selectFirstSport() {
		// Don't select first sport on home page;
		if (!history.location.pathname.includes("land")) {
			return;
		}
		if (!history.location.pathname.includes("my-favorites")) return;

		const selectedSport = this.menu?.children?.filter(
			(sport) => !sport.node.isTopTournament
		);
		this.selectSport(selectedSport?.[0]);
	}

	@action.bound
	onBeforeInitialize() {
		this.openOfferMenu();
	}

	@action.bound
	initializeTopTournaments(
		topTournaments: FeaturedMenuElement[]
	): FormattedMenuNode[] | null {
		if (topTournaments.length == 0) {
			return null;
		}

		const formatMenu = this.formatTopTournamentsMenu(topTournaments || []);

		return formatMenu;
	}

	@action.bound
	onBeforeFetchFinish() {
		// Since store is constantly being reinitialized check first if there is a valid first sport
		if (
			this.selectedSport == null ||
			this.menu.children?.find(
				(sport) => sport.node.id === this.selectedSport?.node.id
			) == null
		) {
			const selectedSport = this.menu?.children?.filter(
				(sport) => !sport.node.isTopTournament
			)?.[0];
			if (!location.pathname.includes("/my-favorites")) {
				this.selectSport(selectedSport);
			}
		}

		if (this.urlSync == null) {
			this.startUrlSync();
		}
	}

	//#endregion fetching data
	// #region formatting
	/**
	 * Create sport - tournaments menu
	 * @param rawMenu
	 * @returns
	 */

	protected formatTopTournamentsMenu(
		rawMenu: FeaturedMenuElement[]
	): FormattedMenuNode[] {
		const menu = rawMenu.map((tt) => {
			let tournamentIdsInOrder: string[] = [];

			const sport: FormattedMenuNode = {
				n: {
					type: "sport",
					isTopTournament: true,

					id: "tt:" + tt.id,
					abrv: tt.abrv,
					count: tt.count || 0,
					name: tt.name || "",
				},
				c: [],
			};

			const category = tt?.items?.map((s) => {
				let categoryIds = "tt";

				const sportLvl: FormattedMenuNode =
					s.type == "sport"
						? {
								n: {
									id: "tt:" + s.id,
									type: "category",
									count: s.count || 0,
									isTopTournament: true,
									name: s.name || "",
									abrv: s.abrv,
								},
								c: [],
							}
						: this.tournamentAsCategory(s);
				categoryIds += ":" + s.id;

				if (!s.id) {
					tournamentIdsInOrder.push(...(s.tournamentsIds || []));
					sportLvl.n.id = "ss:" + s.tournamentsIds?.join(":");
					sportLvl.n.isSpecialOffer = true;
					sportLvl.n.abrv = tt.abrv;
				} else {
					tournamentIdsInOrder.push(s.id);
				}

				sportLvl.c =
					s.items?.map<FormattedMenuNode>((t) => {
						let id = t.id,
							abrv = t.abrv,
							isSpecialOffer = false;

						if (!id) {
							tournamentIdsInOrder.push(
								...(t.tournamentsIds || [])
							);
							id = "ss:" + t.tournamentsIds?.join(":");
							abrv = tt.abrv;
							isSpecialOffer = true;
						} else {
							tournamentIdsInOrder.push(id);
						}
						return {
							n: {
								id,
								abrv,
								isSpecialOffer,

								count: t.count,
								type: "tournament",
								name: t.name,
								isTopTournament: true,
								categoryAbrv: t.sportCategory,
								sportAbrv: t.sport,
							},
							c: [],
						};
					}) || [];

				if (s.type == "sport") {
					sportLvl.n.id = categoryIds;
				}
				return sportLvl;
			}, []);

			sport.c = category;
			sport.n.tournamentIds = tournamentIdsInOrder;
			sport.n.sportAbrv =
				sport.c?.[0]?.n?.sportAbrv || sport.c?.[0]?.n?.abrv;
			return sport;
		});

		return menu;
	}
	protected tournamentAsCategory(t: Item): FormattedMenuNode {
		return {
			n: {
				id: "tac:" + t.id,
				type: "category",
				count: t.count || 0,
				isTopTournament: true,
				name: t.name || "",
				abrv: t.abrv,
				sportAbrv: t.sport,
				categoryAbrv: t.sportCategory,
			},
			c: [],
		};
	}
	// #endregion formatting
	//#region disposers

	@action.bound
	onDispose() {
		this.onBaseDispose();
		this.disposeWhenStoreIsInitialized();
		this.disposeUrlSyn();
	}

	@action.bound
	disposeWhenStoreIsInitialized() {
		if (this.waitForStoreInitialize != null) {
			this.waitForStoreInitialize();
			this.waitForStoreInitialize = null;
		}
	}

	@action.bound
	disposeUrlSyn() {
		if (this.urlSync != null) {
			this.urlSync();
			this.urlSync = null;
		}
	}

	//#endregion disposers

	//#region url update

	@action.bound
	toggleUpdateProcessing() {
		this.isUpdateProcessing = !this.isUpdateProcessing;
	}

	@action.bound
	updateUrl() {
		const url = this.getUrl();
		redirect(url, true);
	}

	protected getUrl() {
		const segments = this.menu.urlStructure;
		// If there are any segments then it will redirect to sports page
		let urlBase = this.getBaseUrl(segments === "");
		const queryParams = history.location.search;
		if (history.location.pathname.includes("my-favorites") && !segments) {
			if (history.location.pathname.includes("events")) {
				return (urlBase += "/my-favorites/events");
			}
			return (urlBase += "/my-favorites/tournaments");
		}
		if (segments == null || segments === "") {
			return urlBase + queryParams;
		}

		const single = this.singleTournamentNode;
		if (
			single != null &&
			!history.location.pathname.includes("my-favorites")
		) {
			const sportNode = single.parent?.parent;
			return (
				buildRoute.tournamentCanonical({
					culture: getCurrentCulture(),
					period: this.period,
					sportAbrv: sportNode?.node?.abrv || "",
					tournamentAbrv: single.node.abrv || "",
					tournamentId: single.node.id,
				}) + queryParams
			);
		}

		return (
			urlBase +
			`/f/${this.menu.children.find((sport) => sport.selectedItemsSet?.size || 0 > 0)?.node?.abrv}${segments}` +
			queryParams
		);
	}

	getBaseUrl(persistPage = false) {
		const period = this.periodSubMenu.selectedPeriodKey;
		const page =
			history.location.pathname.includes("home") && persistPage
				? "home"
				: "sports";
		if (page == "home") {
			const currentHomePageOffer = history.location.pathname.split("/");

			return `/${getCurrentCulture()}/${page}/${period}/${
				currentHomePageOffer[4]
			}`;
		}
		return `/${getCurrentCulture()}/${page}/${period}`;
	}

	//#endregion url update

	//#region update params

	@action.bound
	async setRouterMatchObj(
		routerMatch: match<{
			period: Period;
			segments: string;
			tournamentIds?: string;
			sportAbrv?: string;
			tournamentAbrv?: string;
			tournamentId?: string;
		}>
	) {
		this.isProcessingParams = true;
		const { params, url, path } = routerMatch;
		this.topTournamentTournamentIds = null;
		let topTournamentIds: string[] | null = null;

		if (params.tournamentIds != null) {
			topTournamentIds =
				await this.rootStore.promoOfferViewStore.getTopTournamentIds(
					params.tournamentIds
				);
		}

		// If store is not yet initialized then after initialization is done reset menu based on url
		const resetMenuOnUrl =
			!this.isStoreInitialized ||
			history.action === "POP" ||
			path.includes("tournament") ||
			params.segments == null ||
			params.segments === "";
		this.disposeWhenStoreIsInitialized();

		this.waitForStoreInitialize = when(
			() => this.isStoreInitialized,
			() =>
				runInAction(() => {
					this.setParams(params, resetMenuOnUrl);

					this.topTournamentTournamentIds = topTournamentIds;
					if (path.includes("land")) {
						this.selectFirstSport();
					}

					// Clear menu selection if we switch to home page
					if (path.includes("home")) {
						this.menu.onCheck(false);
						this.menu.collapseTree();
					}

					if (url.includes("my-favorites")) {
						this.openFavoritesMenu();
					}

					this.isProcessingParams = false;
				})
		);
	}

	@action.bound
	setParams(
		params: {
			period: Period;
			segments: string;
			topTournamentId?: string;
			sportAbrv?: string;
			tournamentAbrv?: string;
			tournamentId?: string;
		},
		shouldResetMenuOnUrl: boolean
	) {
		const { period, segments, sportAbrv, tournamentId } = params;

		if (shouldResetMenuOnUrl) {
			this.resetMenuOnUrlParamsFromRouter(segments);
		}
		if (sportAbrv && tournamentId) {
			this.selectTournamentFromUrl(sportAbrv, tournamentId);
		}
		const shouldOpenOfferMenu = location.pathname.includes("my-favorites");
		this.periodSubMenu.setPeriodKey(period, !shouldOpenOfferMenu);
	}

	@action.bound
	selectTournamentFromUrl(sportAbrv: string, tournamentId: string) {
		const sportNode = this.menu.children.find(
			(s) => s.node?.abrv === sportAbrv && !s.node?.isTopTournament
		);
		if (sportNode == null) return;
		for (const categoryNode of sportNode.children) {
			const tournamentNode = categoryNode.children.find(
				(t) =>
					t.node?.id === tournamentId && t.node?.type === "tournament"
			);
			if (tournamentNode != null) {
				tournamentNode.onCheck(true);
				return;
			}
		}
	}

	//#endregion update params

	//#region  url sync

	@action.bound
	startUrlSync(): void {
		this.urlSync = reaction(
			() => {
				return {
					selected: this.menu.urlStructure,
					period: this.periodSubMenu.selectedPeriodKey,
				};
			},
			() => {
				if (this.isUpdateProcessing) {
					this.toggleUpdateProcessing();
					this.updateUrl();
				}
			}
		);
	}

	//#endregion url syn
}
